import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type CartItem = {
  id: string;
  printType: 'fdm' | 'sla';
  thumbnailDataUrl?: string;
  bbox: { x: number; y: number; z: number };
  color: string;
  material: string;
  quality: string;
  infillPercent: number;
  quantity: number;
  unitPriceMXN: number;
  analysisId?: string | null;
  modelUploadPath?: string | null;
  originalFilename?: string | null;
};

type CartState = {
  items: CartItem[];
  expiresAt: number | null;
};

type CartContextValue = {
  items: CartItem[];
  count: number;
  total: number;
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clear: () => void;
  load: () => void;
  save: () => void;
  submitted: boolean;
  markSubmitted: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = 'i3d.cart.v1';
const TTL_MS = 14 * 24 * 60 * 60 * 1000;

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<CartState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { items: [], expiresAt: null };
      const parsed: CartState = JSON.parse(raw);
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return { items: [], expiresAt: null };
      }
      return parsed;
    } catch {
      return { items: [], expiresAt: null };
    }
  });
  const initializedRef = useRef(false);
  const [uiSubmitted, setUiSubmitted] = useState(false);

  const load = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: CartState = JSON.parse(raw);
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        setState({ items: [], expiresAt: null });
        return;
      }
      setState(parsed);
    } catch {
      setState({ items: [], expiresAt: null });
    }
  };

  const save = () => {
    try {
      const expiresAt = Date.now() + TTL_MS;
      const snapshot: CartState = { items: state.items, expiresAt };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      setState(snapshot);
    } catch {}
  };

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
    }
  }, []);

  const addItem = (item: CartItem) => {
    setState((prev) => {
      const nextItems = [...prev.items, item];
      const expiresAt = Date.now() + TTL_MS;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: nextItems, expiresAt })); } catch {}
      return { items: nextItems, expiresAt };
    });
  };

  const removeItem = (id: string) => {
    setState((prev) => {
      const nextItems = prev.items.filter((i) => i.id !== id);
      const expiresAt = prev.expiresAt ?? (Date.now() + TTL_MS);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: nextItems, expiresAt })); } catch {}
      return { items: nextItems, expiresAt };
    });
  };

  const clear = () => {
    setState({ items: [], expiresAt: null });
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  useEffect(() => {
    if (!initializedRef.current) return;
    save();
  }, [state.items]);

  const value = useMemo<CartContextValue>(() => ({
    items: state.items,
    count: state.items.length,
    total: state.items.reduce((sum, it) => sum + it.unitPriceMXN * it.quantity, 0),
    addItem,
    removeItem,
    clear,
    load,
    save,
    submitted: uiSubmitted,
    markSubmitted: () => setUiSubmitted(true),
  }), [state.items, uiSubmitted]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
